import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useState, useRef, useEffect } from "react";
import { Upload, FileText, ArrowRight, RefreshCw, Loader2, CheckCircle, Trash2, Check, AlertCircle, Calendar, Copy, Settings2, HelpCircle } from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUserVehicles } from "@/hooks/useUserVehicles";
import PhotoUpload from "@/components/PhotoUpload";
import PhotoIntelligenceInfo from "@/components/PhotoIntelligenceInfo";
import RunWaypointReview from "@/components/RunWaypointReview";
import TrailSegmentFeedback from "@/components/TrailSegmentFeedback";
import RunPrivacyControl from "@/components/RunPrivacyControl";
import { useRunWaypoints, type RunWaypoint } from "@/hooks/useRunWaypoints";
import { useUserMetrics } from "@/hooks/useUserMetrics";
import { ROUTES } from "@/lib/routes";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface PhotoFile {
  file: File;
  preview: string;
  id: string;
}

const LogRun = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { runId: urlRunId } = useParams();
  const [searchParams] = useSearchParams();
  const editRunId = urlRunId || searchParams.get('edit');
  const isEditMode = !!editRunId;
  
  const [currentStep, setCurrentStep] = useState(isEditMode ? 4 : 1);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string>('pending');
  const [runId, setRunId] = useState<string | null>(editRunId || null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [existingPhotoUrls, setExistingPhotoUrls] = useState<string[]>([]);
  const [runData, setRunData] = useState({
    title: '',
    description: '',
    visibility: 'public' as 'public' | 'private' | 'unlisted' | 'anonymous',
    vehicleId: ''
  });
  
  // Fetch user's vehicles
  const { vehicles, refetch: refetchVehicles } = useUserVehicles(user?.id || '');
  const { metrics } = useUserMetrics(1);
  const [guideDismissed, setGuideDismissed] = useState(false);
  const showFirstTimeGuide = !isEditMode && !guideDismissed && metrics.totalRuns === 0;
  const [aiGeneratedTitle, setAiGeneratedTitle] = useState<string>('');
  const [aiGeneratedDescription, setAiGeneratedDescription] = useState<string>('');
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAnalyzingPhotos, setIsAnalyzingPhotos] = useState(false);
  const [trackGeometry, setTrackGeometry] = useState<any>(null);
  const [detectedRunDate, setDetectedRunDate] = useState<Date | null>(null);

  // Auto-select vehicle if user has vehicles
  useEffect(() => {
    if (vehicles && vehicles.length > 0 && !runData.vehicleId && !isEditMode) {
      const featuredVehicle = vehicles.find(v => v.is_featured);
      if (featuredVehicle) {
        setRunData(prev => ({...prev, vehicleId: featuredVehicle.id}));
      } else {
        setRunData(prev => ({...prev, vehicleId: vehicles[0].id}));
      }
    }
  }, [vehicles, isEditMode]);

  // Load existing run data if editing
  useEffect(() => {
    if (isEditMode && editRunId) {
      loadExistingRun(editRunId);
    }
  }, [isEditMode, editRunId]);

  const loadExistingRun = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('runs')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (data.user_id !== user?.id) {
        toast({
          title: "Access denied",
          description: "You can only edit your own runs.",
          variant: "destructive",
        });
        navigate('/dashboard');
        return;
      }

      setRunData({
        title: data.title || '',
        description: data.description || '',
        visibility: data.visibility || 'public',
        vehicleId: data.vehicle_id || ''
      });

      if (data.photo_urls && Array.isArray(data.photo_urls)) {
        setExistingPhotoUrls(data.photo_urls.map(url => String(url)));
      }

      setProcessingStatus('completed');
      
      // Load track geometry for waypoint editor
      if (data.geometry) {
        setTrackGeometry(data.geometry);
      }
    } catch (error) {
      console.error('Error loading run:', error);
      toast({
        title: "Error",
        description: "Failed to load run data.",
        variant: "destructive",
      });
      navigate('/dashboard');
    }
  };

  // Poll for processing status
  useEffect(() => {
    if (!runId || processingStatus === 'completed' || processingStatus === 'error' || isEditMode) {
      return;
    }

    const pollInterval = setInterval(async () => {
      const { data, error } = await supabase
        .from('runs')
        .select('processing_status, processing_error, geometry')
        .eq('id', runId)
        .single();

      if (error) {
        console.error('Error polling run status:', error);
        return;
      }

      if (data) {
        setProcessingStatus(data.processing_status);
        
        if (data.processing_status === 'completed') {
          setIsProcessing(false);
          clearInterval(pollInterval);
          if (data.geometry) {
            setTrackGeometry(data.geometry);
          }
          toast({
            title: "Processing complete",
            description: "Your run has been processed successfully",
          });
          // Auto-advance to Step 3 (Waypoints)
          setCurrentStep(3);
        } else if (data.processing_status === 'error') {
          setIsProcessing(false);
          clearInterval(pollInterval);
          toast({
            title: "Processing failed",
            description: data.processing_error || "Failed to process GPX file. Please try again.",
            variant: "destructive",
          });
          // Allow user to go back and try again
          setCurrentStep(1);
          setRunId(null);
          setSelectedFile(null);
        }
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [runId, processingStatus, toast]);

  const generateAIContent = async (regenerate = false) => {
    if (!runId) return;
    
    setIsGeneratingContent(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-run-content', {
        body: { runId, regenerate }
      });

      if (error) throw error;

      if (data) {
        setAiGeneratedTitle(data.title);
        setAiGeneratedDescription(data.description);
        setRunData(prev => ({
          ...prev,
          title: data.title,
          description: data.description
        }));
      }
    } catch (error) {
      console.error('AI generation error:', error);
      toast({
        title: "AI generation failed",
        description: "Could not generate title and description. You can enter them manually.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingContent(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto text-center">
            <h1 className="text-3xl font-bold text-foreground mb-4">Log a Run</h1>
            <p className="text-muted-foreground mb-8">
              Please sign in to log your runs.
            </p>
            <Button onClick={() => navigate('/auth')}>
              Sign In
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const parseGpxForDate = async (file: File): Promise<Date | null> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const parser = new DOMParser();
          const xml = parser.parseFromString(e.target?.result as string, 'text/xml');
          const timeElement = xml.querySelector('trkpt time');
          if (timeElement?.textContent) {
            resolve(new Date(timeElement.textContent));
          } else {
            resolve(null);
          }
        } catch {
          resolve(null);
        }
      };
      reader.onerror = () => resolve(null);
      reader.readAsText(file);
    });
  };

  const handleCopyDate = () => {
    if (detectedRunDate) {
      const formattedDate = format(detectedRunDate, 'yyyy-MM-dd');
      navigator.clipboard.writeText(formattedDate);
      toast({
        title: "Date copied!",
        description: `${formattedDate} copied to clipboard`,
      });
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.name.toLowerCase().endsWith('.gpx')) {
        setSelectedFile(file);
        const runDate = await parseGpxForDate(file);
        setDetectedRunDate(runDate);
      } else {
        toast({
          title: "Invalid file type",
          description: "Please select a GPX file.",
          variant: "destructive",
        });
      }
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.name.toLowerCase().endsWith('.gpx')) {
      setSelectedFile(file);
      const runDate = await parseGpxForDate(file);
      setDetectedRunDate(runDate);
    } else {
      toast({
        title: "Invalid file type",
        description: "Please select a GPX file.",
        variant: "destructive",
      });
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleUploadGPX = async () => {
    if (!selectedFile) return;

    setIsUploading(true);

    try {
      // Upload GPX file to Supabase Storage
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('gpx-files')
        .upload(fileName, selectedFile);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('gpx-files')
        .getPublicUrl(fileName);

      // Insert run record
      const { data: runRecord, error: runError } = await supabase
        .from('runs')
        .insert({
          user_id: user.id,
          equipment_type: 'suv' as const, // Default value
          source: 'gpx_upload',
          gpx_file_url: publicUrl,
          processing_status: 'pending' as const,
        })
        .select()
        .single();

      if (runError) throw runError;

      setRunId(runRecord.id);

      // Upload photos if any
      let photoUrls: string[] = [];
      if (photos.length > 0) {
        const photoUploadPromises = photos.map(async (photo) => {
          const photoExt = photo.file.name.split('.').pop();
          const photoFileName = `${user.id}/${runRecord.id}/${Date.now()}-${photo.id}.${photoExt}`;
          
          const { error: photoUploadError } = await supabase.storage
            .from('run-photos')
            .upload(photoFileName, photo.file);

          if (photoUploadError) throw photoUploadError;

          const { data: { publicUrl: photoPublicUrl } } = supabase.storage
            .from('run-photos')
            .getPublicUrl(photoFileName);

          return photoPublicUrl;
        });

        photoUrls = await Promise.all(photoUploadPromises);

        // Update run with photo URLs
        const { error: updateError } = await supabase
          .from('runs')
          .update({ photo_urls: photoUrls })
          .eq('id', runRecord.id);

        if (updateError) throw updateError;
      }

      // Trigger GPX processing
      const { error: processError } = await supabase.functions.invoke('process-gpx', {
        body: {
          runId: runRecord.id,
          gpxFileUrl: publicUrl,
        },
      });

      if (processError) {
        console.error('GPX processing initiation failed:', processError);
        toast({
          title: "Processing initiation failed",
          description: "There was an issue starting the GPX processing.",
          variant: "destructive",
        });
      } else {
        setIsProcessing(true);
        
        // If we have photos, trigger photo analysis after a short delay to let GPX processing complete
        if (photoUrls.length > 0) {
          setTimeout(async () => {
            try {
              console.log('Triggering photo analysis for initial photos...');
              const { error: analysisError } = await supabase.functions.invoke('analyze-run-photos', {
                body: { runId: runRecord.id, photoUrls }
              });
              
              if (analysisError) {
                console.error('Photo analysis error:', analysisError);
              } else {
                console.log('Photo analysis completed');
              }
            } catch (error) {
              console.error('Failed to trigger photo analysis:', error);
            }
          }, 3000); // Wait 3 seconds for GPX processing to insert waypoints
        }
        
        setCurrentStep(2); // Move to processing step
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Failed to upload GPX file.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handlePhotoUploadAndNext = async () => {
    setIsUploading(true);

    try {
      // Upload new photos to storage if any
      let newPhotoUrls: string[] = [];
      if (photos.length > 0) {
        const photoUploadPromises = photos.map(async (photo) => {
          const photoExt = photo.file.name.split('.').pop();
          const photoFileName = `${user.id}/${Date.now()}-${photo.id}.${photoExt}`;
          
          const { error: photoUploadError } = await supabase.storage
            .from('run-photos')
            .upload(photoFileName, photo.file);

          if (photoUploadError) throw photoUploadError;

          const { data: { publicUrl: photoPublicUrl } } = supabase.storage
            .from('run-photos')
            .getPublicUrl(photoFileName);

          return photoPublicUrl;
        });

        newPhotoUrls = await Promise.all(photoUploadPromises);
      }

      // Combine existing photos with new photos
      const allPhotoUrls = [...existingPhotoUrls, ...newPhotoUrls];

      // Update run with photos only
      const { error: updateError } = await supabase
        .from('runs')
        .update({
          photo_urls: allPhotoUrls,
        })
        .eq('id', runId);

      if (updateError) throw updateError;

      // Analyze photos and create waypoints if we have new photos
      if (newPhotoUrls.length > 0) {
        setIsAnalyzingPhotos(true);
        try {
          const { data: analysisData, error: analysisError } = await supabase.functions.invoke('analyze-run-photos', {
            body: { runId, photoUrls: newPhotoUrls }
          });

          if (analysisError) {
            console.error('Photo analysis error:', analysisError);
            toast({
              title: "Photo analysis failed",
              description: "We couldn't analyze your photos automatically, but you can still add waypoints manually.",
              variant: "destructive",
            });
          } else if (analysisData?.waypointsCreated > 0) {
            toast({
              title: "Photos analyzed!",
              description: `Created ${analysisData.waypointsCreated} waypoint(s) from your photos.`,
            });
          }
        } catch (analysisError) {
          console.error('Photo analysis error:', analysisError);
        } finally {
          setIsAnalyzingPhotos(false);
        }
      }

      // Fetch track geometry if not already loaded
      if (!trackGeometry) {
        const { data: runData } = await supabase
          .from('runs')
          .select('geometry')
          .eq('id', runId)
          .single();
        
        if (runData?.geometry) {
          setTrackGeometry(runData.geometry);
        }
      }

      // Move to waypoint editing step
      setCurrentStep(3);
    } catch (error) {
      console.error('Photo upload error:', error);
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Failed to upload photos.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveDetails = async () => {
    if (!runId || !runData.vehicleId || !runData.title) {
      toast({
        title: "Missing information",
        description: "Please complete all required fields.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);

    try {
      // Update run with details
      const { error: updateError } = await supabase
        .from('runs')
        .update({
          title: runData.title,
          description: runData.description,
          vehicle_id: runData.vehicleId,
        })
        .eq('id', runId);

      if (updateError) throw updateError;

      // Move to trail feedback step
      setCurrentStep(5);
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Save failed",
        description: error instanceof Error ? error.message : "Failed to save run details.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSkipFeedbackAndFinish = async () => {
    // Set enhancement_completed to trigger email notification
    if (runId) {
      await supabase
        .from('runs')
        .update({ enhancement_completed: true })
        .eq('id', runId);
    }

    toast({
      title: "Run saved!",
      description: "Your run has been successfully logged.",
    });
    navigate(`/runs/${runId}`);
  };

  const handleFinalSave = async (finalWaypoints: Partial<RunWaypoint>[]) => {
    if (!runId) return;

    setIsUploading(true);

    try {
      // Waypoints are already saved by the editor
      toast({
        title: "Run saved!",
        description: "Your run has been successfully logged with waypoints.",
      });

      navigate(`/runs/${runId}`);
    } catch (error) {
      console.error('Final save error:', error);
      toast({
        title: "Save failed",
        description: error instanceof Error ? error.message : "Failed to finalize run.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteRun = async () => {
    if (!runId) return;

    setIsDeleting(true);

    try {
      // 1. First, get the run's photo URLs from the database
      const { data: runPhotoData } = await supabase
        .from('runs')
        .select('photo_urls, hero_photo_url')
        .eq('id', runId)
        .single();

      // 2. Delete photos from storage if they exist
      if (runPhotoData?.photo_urls && Array.isArray(runPhotoData.photo_urls) && runPhotoData.photo_urls.length > 0) {
        const photoPathsToDelete = runPhotoData.photo_urls.map((url: string) => {
          // Extract path from URL: everything after 'run-photos/'
          const parts = url.split('/run-photos/');
          return parts.length > 1 ? decodeURIComponent(parts[1]) : null;
        }).filter(Boolean) as string[];

        if (photoPathsToDelete.length > 0) {
          const { error: storageError } = await supabase.storage
            .from('run-photos')
            .remove(photoPathsToDelete);

          if (storageError) {
            console.warn('Failed to delete some photos:', storageError);
            // Continue with run deletion even if photo cleanup fails
          }
        }
      }

      // 3. Delete in cascade order: waypoints, segments, tracks, then run
      const { error: waypointsError } = await supabase
        .from('run_waypoints')
        .delete()
        .eq('run_id', runId);

      if (waypointsError) throw waypointsError;

      const { error: segmentsError } = await supabase
        .from('run_segments')
        .delete()
        .eq('run_id', runId);

      if (segmentsError) throw segmentsError;

      const { error: tracksError } = await supabase
        .from('run_tracks')
        .delete()
        .eq('run_id', runId);

      if (tracksError) throw tracksError;

      const { error: runError } = await supabase
        .from('runs')
        .delete()
        .eq('id', runId);

      if (runError) throw runError;

      toast({
        title: "Run deleted",
        description: "Your run and all associated photos have been permanently deleted.",
      });

      navigate('/dashboard');
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: "Delete failed",
        description: error instanceof Error ? error.message : "Failed to delete run.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const getProcessingMessage = () => {
    switch (processingStatus) {
      case 'processing':
        return 'Analyzing your GPX track...';
      case 'completed':
        return 'Processing complete!';
      case 'error':
        return 'Processing failed';
      default:
        return 'Preparing to process...';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-3 md:px-4 py-3 md:py-8">
        <div className="max-w-2xl mx-auto">
          {/* Page title - hidden on mobile, visible on desktop */}
          <div className="hidden md:flex mb-8 items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {isEditMode ? 'Edit Run' : 'Log a Run'}
              </h1>
              <p className="text-muted-foreground">
                {isEditMode ? 'Update your off-road adventure details' : 'Share your off-road adventure with the community'}
              </p>
            </div>
            {isEditMode && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this run?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this run and all associated data including track points, waypoints, and photos. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteRun}
                      disabled={isDeleting}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {isDeleting ? 'Deleting...' : 'Delete Run'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>

          {/* Mobile edit mode header with delete button */}
          {isEditMode && (
            <div className="flex md:hidden mb-4 items-center justify-between">
              <h1 className="text-xl font-bold text-foreground">Edit Run</h1>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this run?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this run and all associated data including track points, waypoints, and photos. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteRun}
                      disabled={isDeleting}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {isDeleting ? 'Deleting...' : 'Delete Run'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}

          {/* Progress Indicator */}
          {!isEditMode && (
            <div className="flex items-center justify-between mb-4 md:mb-6 overflow-x-auto pb-2">
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 1 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                  1
                </div>
                <span className="hidden md:inline text-sm font-medium">Upload</span>
              </div>
              <div className="flex-1 h-0.5 bg-muted mx-2 md:mx-4 min-w-[20px]"></div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 2 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                  2
                </div>
                <span className="hidden md:inline text-sm font-medium">Processing</span>
              </div>
              <div className="flex-1 h-0.5 bg-muted mx-2 md:mx-4 min-w-[20px]"></div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 3 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                  3
                </div>
                <span className="hidden md:inline text-sm font-medium">Waypoints</span>
              </div>
              <div className="flex-1 h-0.5 bg-muted mx-2 md:mx-4 min-w-[20px]"></div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 4 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                  4
                </div>
                <span className="hidden md:inline text-sm font-medium">Details</span>
              </div>
              <div className="flex-1 h-0.5 bg-muted mx-2 md:mx-4 min-w-[20px]"></div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 5 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                  5
                </div>
                <span className="hidden md:inline text-sm font-medium">Feedback</span>
              </div>
            </div>
          )}
          
          <div className="space-y-6">
            {/* First-time user guide prompt */}
            {currentStep === 1 && showFirstTimeGuide && (
              <div className="flex items-start gap-3 rounded-lg border border-primary/20 bg-primary/5 p-4">
                <HelpCircle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">New to GPS tracking?</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Learn how to record your off-road adventures with a GPX file.
                  </p>
                  <div className="flex items-center gap-3 mt-2">
                    <Link to={ROUTES.gpxTracksGuide} className="text-sm font-medium text-primary hover:underline">
                      View recording guide →
                    </Link>
                    <button onClick={() => setGuideDismissed(true)} className="text-sm text-muted-foreground hover:text-foreground">
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: GPX Upload - Skip in edit mode */}
            {currentStep === 1 && !isEditMode && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Upload GPX Track
                  </CardTitle>
                  <CardDescription>
                    Upload your GPS track file to get started
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div 
                    className="border-2 border-dashed border-border rounded-lg p-12 text-center cursor-pointer hover:border-primary/50 transition-colors"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".gpx"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    
                    {selectedFile ? (
                      <div className="space-y-2">
                        <FileText className="h-12 w-12 text-green-500 mx-auto" />
                        <p className="text-lg font-medium text-foreground">{selectedFile.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <Upload className="h-12 w-12 text-muted-foreground mx-auto" />
                        <div>
                          <p className="text-lg font-medium text-foreground mb-2">
                            Drag & drop your GPX file here
                          </p>
                          <p className="text-muted-foreground mb-4">or</p>
                          <Button variant="outline">Choose File</Button>
                        </div>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-4">
                    Supported formats: GPX files up to 10MB
                  </p>

                  {selectedFile && (
                    <div className="mt-6 space-y-4">
                      <div>
                        <h3 className="text-lg font-medium mb-2">Photos (Optional)</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Add photos from your run to help generate waypoints and enhance your log
                        </p>
                        
                        {detectedRunDate && (
                          <div className="mb-4 p-3 bg-muted/50 rounded-lg flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">
                                Looks like your run was on <strong className="text-foreground">{format(detectedRunDate, 'yyyy-MM-dd')}</strong>
                              </span>
                            </div>
                            <Button variant="outline" size="sm" onClick={handleCopyDate}>
                              <Copy className="h-3 w-3 mr-1" />
                              Copy Date
                            </Button>
                          </div>
                        )}
                        
                        <PhotoUpload 
                          photos={photos}
                          onPhotosChange={setPhotos}
                          maxPhotos={10}
                        />
                      </div>
                    </div>
                  )}

                  <div className="mt-6 flex justify-end">
                    <Button 
                      onClick={handleUploadGPX}
                      disabled={!selectedFile || isUploading}
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          Upload & Process
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Processing */}
            {currentStep === 2 && !isEditMode && (
              <Card>
                <CardHeader>
                  <CardTitle>
                    Processing Your Run
                  </CardTitle>
                  <CardDescription>
                    Analyzing GPS track{photos.length > 0 ? ' and processing photos' : ''}...
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Waypoint Review */}
            {currentStep === 3 && !isEditMode && runId && (
              <>
                <RunWaypointReview
                  runId={runId}
                  trackGeometry={trackGeometry}
                  onComplete={() => setCurrentStep(4)}
                  onBack={() => setCurrentStep(2)}
                />
              </>
            )}

            {/* Step 4: Run Details */}
            {currentStep === 4 && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>Run Details</span>
                      <div className="flex gap-2">
                        {!aiGeneratedTitle && !aiGeneratedDescription && !isGeneratingContent && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => generateAIContent()}
                            disabled={isGeneratingContent}
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Generate AI Content
                          </Button>
                        )}
                        {(aiGeneratedTitle || aiGeneratedDescription) && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => generateAIContent(true)}
                            disabled={isGeneratingContent}
                          >
                            <RefreshCw className={`h-4 w-4 mr-2 ${isGeneratingContent ? 'animate-spin' : ''}`} />
                            Regenerate AI
                          </Button>
                        )}
                      </div>
                    </CardTitle>
                    <CardDescription>
                      {isGeneratingContent 
                        ? "AI is generating suggestions based on your route..." 
                        : "Review and edit the AI-generated content or add your own"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Run Title *</Label>
                      <Input
                        id="title"
                        placeholder="Enter a title for your run"
                        value={runData.title}
                        onChange={(e) => setRunData(prev => ({...prev, title: e.target.value}))}
                        disabled={isGeneratingContent}
                      />
                      {aiGeneratedTitle && runData.title === aiGeneratedTitle && (
                        <p className="text-xs text-muted-foreground">✨ AI-generated suggestion</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="vehicle">Vehicle Used *</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => refetchVehicles()}
                          className="h-8 px-2"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      </div>
                      <Select 
                        value={runData.vehicleId || "not-specified"} 
                        onValueChange={(value) => setRunData(prev => ({...prev, vehicleId: value === "not-specified" ? "" : value}))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a vehicle" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not-specified">Not specified</SelectItem>
                          {vehicles?.map((vehicle) => (
                            <SelectItem key={vehicle.id} value={vehicle.id}>
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {vehicle.nickname || `${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                                </span>
                                {vehicle.nickname && (
                                  <span className="text-xs text-muted-foreground">
                                    {vehicle.year} {vehicle.make} {vehicle.model}
                                  </span>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Don't see your vehicle?{" "}
                        <a 
                          href="/profile" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Manage your garage
                        </a>
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        placeholder="Describe your experience, conditions, notable features..."
                        value={runData.description}
                        onChange={(e) => setRunData(prev => ({...prev, description: e.target.value}))}
                        rows={5}
                        disabled={isGeneratingContent}
                      />
                      {aiGeneratedDescription && runData.description === aiGeneratedDescription && (
                        <p className="text-xs text-muted-foreground">✨ AI-generated suggestion</p>
                      )}
                    </div>

                    {/* Privacy Control - Organizer tier */}
                    <div className="pt-4 border-t border-border">
                      <RunPrivacyControl
                        visibility={runData.visibility}
                        onChange={(visibility) => setRunData(prev => ({...prev, visibility}))}
                        disabled={isGeneratingContent}
                      />
                    </div>

                    {/* Advanced Edit Link - only in edit mode */}
                    {isEditMode && runId && (
                      <div className="pt-4 border-t border-border">
                        <Link 
                          to={`/runs/${runId}/edit`}
                          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <Settings2 className="h-4 w-4" />
                          Advanced editing (track cleanup, waypoints, photos)
                        </Link>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Action Buttons */}
                <div className="flex gap-4">
                  {!isEditMode && (
                    <Button 
                      variant="outline"
                      onClick={() => setCurrentStep(3)}
                    >
                      Back
                    </Button>
                  )}
                  {isEditMode && (
                    <Button 
                      variant="outline"
                      onClick={() => navigate(`/runs/${runId}`)}
                      disabled={isUploading}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button 
                    onClick={isEditMode ? async () => {
                      if (!runId || !runData.vehicleId || !runData.title) {
                        toast({
                          title: "Missing information",
                          description: "Please complete all required fields.",
                          variant: "destructive",
                        });
                        return;
                      }
                      setIsUploading(true);
                      try {
                        const { error: updateError } = await supabase
                          .from('runs')
                          .update({
                            title: runData.title,
                            description: runData.description,
                            vehicle_id: runData.vehicleId || null,
                            visibility: runData.visibility,
                          })
                          .eq('id', runId);
                        if (updateError) throw updateError;
                        toast({
                          title: "Run updated!",
                          description: "Your run has been successfully updated.",
                        });
                        navigate(`/runs/${runId}`);
                      } catch (error) {
                        console.error('Update error:', error);
                        toast({
                          title: "Update failed",
                          description: error instanceof Error ? error.message : "Failed to update run.",
                          variant: "destructive",
                        });
                      } finally {
                        setIsUploading(false);
                      }
                    } : handleSaveDetails} 
                    disabled={isUploading || isGeneratingContent || !runData.vehicleId || !runData.title}
                    className="flex-1"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {isEditMode ? 'Updating...' : 'Saving...'}
                      </>
                    ) : isGeneratingContent ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating AI content...
                      </>
                    ) : isEditMode ? (
                      'Update Run'
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}

            {/* Step 5: Trail Difficulty Feedback */}
            {currentStep === 5 && !isEditMode && runId && (
              <>
                <TrailSegmentFeedback runId={runId} />

                <div className="flex flex-col md:flex-row gap-4">
                  <Button 
                    variant="outline"
                    onClick={() => setCurrentStep(4)}
                    className="w-full md:w-auto"
                  >
                    Back
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={handleSkipFeedbackAndFinish}
                    className="w-full md:flex-1"
                  >
                    Skip & Finish
                  </Button>
                  <Button 
                    onClick={handleSkipFeedbackAndFinish}
                    className="w-full md:flex-1"
                  >
                    Save & Finish
                    <Check className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default LogRun;